import { useMemo } from 'react';
import { T, useGT } from 'gt-react';
import { Link } from '@tanstack/react-router';
import paths from 'src/paths';
import { Label } from 'src/components/ui/label';
import { Input } from 'src/components/ui/input';
import { Checkbox } from 'src/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import {
  MultiSelect,
  type MultiSelectOption,
} from 'src/components/common/MultiSelect';
import { useUsersInOrg } from '@/api/rpc/hooks/core/useUsersInOrg';
import { useUserGroupsList } from '@/api/rpc/hooks/organization/useUserGroups';
import { useOrganizationContacts } from '@/api/rpc/hooks/communications/useOrganizationContacts';
import { EmailTemplateSelectWithLink } from './EmailTemplateSelectWithLink';

export type NotifyChannel = 'in_app' | 'email';

export interface NotifyRecipientsValue {
  recipientUserIds: string[];
  recipientUserGroupIds: string[];
  recipientContactIds: string[];
  channels: NotifyChannel[];
  senderMailbox: string;
  emailTemplateId: string;
  emailSubject: string;
}

export interface NotifyMailboxOption {
  upn: string;
  label: string;
}

/**
 * Recipient + channel + email-delivery fields shared by the Notify workflow
 * step's config panel and the ad-hoc "Notify…" dialog, so both surfaces stay
 * identical. Controlled: the caller owns the value and receives patches.
 * Mailbox options come from the caller (the canvas lists registered Graph
 * UPNs; the dialog lists the org's sendable mailboxes for a category).
 */
export function NotifyRecipientsFields({
  value,
  onChange,
  mailboxOptions,
  emailSubjectHint,
  /** Rendered inside the email section, above the mailbox field. */
  emailSectionLead,
  /**
   * Hide the email-template picker when the caller owns template selection
   * elsewhere — the ad-hoc dialog picks the template as the message itself,
   * for every channel, and mirrors it into `value.emailTemplateId` so the
   * subject field still follows the no-template convention.
   */
  hideEmailTemplate = false,
}: {
  value: NotifyRecipientsValue;
  onChange: (patch: Partial<NotifyRecipientsValue>) => void;
  mailboxOptions: NotifyMailboxOption[];
  emailSubjectHint?: string;
  emailSectionLead?: React.ReactNode;
  hideEmailTemplate?: boolean;
}) {
  const gt = useGT();
  const {
    recipientUserIds,
    recipientUserGroupIds,
    recipientContactIds,
    channels,
    senderMailbox,
    emailTemplateId,
    emailSubject,
  } = value;

  const toggleChannel = (channel: NotifyChannel) => {
    const has = channels.includes(channel);
    const next = has
      ? channels.filter((c) => c !== channel)
      : [...channels, channel];
    // At least one channel is required — refuse to clear the last one
    // rather than letting an invalid selection surface at send time.
    if (next.length === 0) return;
    onChange({ channels: next });
  };

  const { data: users } = useUsersInOrg();
  const userOptions: MultiSelectOption[] = useMemo(
    () =>
      (users ?? []).map((u) => ({
        id: u.id,
        label: u.email ? `${u.name} <${u.email}>` : u.name,
      })),
    [users],
  );
  const selectedUserOptions = useMemo(
    () => userOptions.filter((o) => recipientUserIds.includes(o.id)),
    [userOptions, recipientUserIds],
  );

  const { data: groups } = useUserGroupsList();
  const groupOptions: MultiSelectOption[] = useMemo(
    () => (groups ?? []).map((g) => ({ id: g.id, label: g.name })),
    [groups],
  );
  const selectedGroupOptions = useMemo(
    () => groupOptions.filter((o) => recipientUserGroupIds.includes(o.id)),
    [groupOptions, recipientUserGroupIds],
  );

  // Address-book (internal) contacts — email-only recipients. Contacts
  // without an email can't receive anything, so they're not offered.
  const { contacts } = useOrganizationContacts();
  const contactOptions: MultiSelectOption[] = useMemo(
    () =>
      contacts
        .filter((c) => !!c.email)
        .map((c) => ({ id: c.id, label: `${c.name} <${c.email}>` })),
    [contacts],
  );
  const selectedContactOptions = useMemo(
    () => contactOptions.filter((o) => recipientContactIds.includes(o.id)),
    [contactOptions, recipientContactIds],
  );

  return (
    <>
      <div className="space-y-2">
        <Label className="text-sm font-medium">
          <T context="notify step config field">Users</T>
        </Label>
        <MultiSelect
          options={userOptions}
          value={selectedUserOptions}
          onChange={(selected) =>
            onChange({ recipientUserIds: selected.map((s) => s.id) })
          }
          placeholder={gt('Pick one or more users', {
            context: 'notify users picker placeholder',
          })}
          searchPlaceholder={gt('Search by name or email', {
            context: 'notify users picker search placeholder',
          })}
          fullWidth
        />
      </div>

      <div className="space-y-2">
        <Label className="text-sm font-medium">
          <T context="notify step config field">User groups</T>
        </Label>
        <MultiSelect
          options={groupOptions}
          value={selectedGroupOptions}
          onChange={(selected) =>
            onChange({ recipientUserGroupIds: selected.map((s) => s.id) })
          }
          placeholder={gt('Pick one or more groups', {
            context: 'notify groups picker placeholder',
          })}
          searchPlaceholder={gt('Search by group name', {
            context: 'notify groups picker search placeholder',
          })}
          fullWidth
        />
        <p className="text-xs text-muted-foreground">
          <T context="notify recipients hint">
            Everyone picked above — users plus all group members — receives the
            notification. Group membership resolves at send time.
          </T>
        </p>
      </div>

      <div className="space-y-2">
        <Label className="text-sm font-medium">
          <T context="notify step config field">Address book contacts</T>
        </Label>
        <MultiSelect
          options={contactOptions}
          value={selectedContactOptions}
          onChange={(selected) =>
            onChange({ recipientContactIds: selected.map((s) => s.id) })
          }
          placeholder={gt('Pick one or more contacts', {
            context: 'notify contacts picker placeholder',
          })}
          searchPlaceholder={gt('Search by name or email', {
            context: 'notify contacts picker search placeholder',
          })}
          fullWidth
        />
        <p className="text-xs text-muted-foreground">
          <T context="notify contacts hint">
            Internal contacts from the org address book (Settings → Address
            Book). Contacts are not users, so they only receive the email
            channel.
          </T>
        </p>
      </div>

      <div className="space-y-2">
        <Label className="text-sm font-medium">
          <T context="notify step config field">Channels</T>{' '}
          <span className="text-destructive">*</span>
        </Label>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={channels.includes('in_app')}
              onCheckedChange={() => toggleChannel('in_app')}
            />
            <T context="notify channel option">In-app</T>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={channels.includes('email')}
              onCheckedChange={() => toggleChannel('email')}
            />
            <T context="notify channel option">Email</T>
          </label>
        </div>
      </div>

      {channels.includes('email') && (
        <div className="space-y-3 rounded-md border p-3">
          {emailSectionLead}
          <div className="space-y-2">
            <Label className="text-sm font-medium">
              <T context="notify step config field">Mailbox (UPN)</T>{' '}
              <span className="text-destructive">*</span>
            </Label>
            {mailboxOptions.length === 0 ? (
              <p className="text-xs text-destructive">
                <T context="notify no graph mailboxes hint">
                  No Microsoft Graph mailboxes are registered for this org.
                  Configure one in Settings → Email registration before enabling
                  the email channel.
                </T>
              </p>
            ) : (
              <Select
                value={senderMailbox || undefined}
                onValueChange={(v) => onChange({ senderMailbox: v })}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={gt('Pick a mailbox', {
                      context: 'notify sender mailbox placeholder',
                    })}
                  />
                </SelectTrigger>
                <SelectContent>
                  {mailboxOptions.map((opt) => (
                    <SelectItem key={opt.upn} value={opt.upn}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {!hideEmailTemplate && (
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <Label
                  htmlFor="notifyEmailTemplateId"
                  className="text-sm font-medium"
                >
                  <T context="notify step config field">Email template</T>
                </Label>
                <p className="text-[11px] text-muted-foreground">
                  <T context="optional field hint">Optional</T>
                </p>
              </div>
              <EmailTemplateSelectWithLink
                id="notifyEmailTemplateId"
                value={emailTemplateId}
                onChange={(v) => onChange({ emailTemplateId: v ?? '' })}
                noneLabel={gt('No template (plain title/body)', {
                  context: 'notify email template none option',
                })}
              />
              <p className="text-[11px] text-muted-foreground">
                {emailTemplateId ? (
                  <T context="help text when a notify email template is selected">
                    The selected template provides both the body and the
                    subject.
                  </T>
                ) : (
                  <T context="help text for the notify no-template option">
                    Sends the interpolated title/body as a simple email. Choose
                    a template for a richer body, or set a custom subject below.
                  </T>
                )}{' '}
                <Link
                  to={paths.authenticated.settings.emailTemplates}
                  className="underline"
                >
                  <T context="link to email templates settings">
                    Edit templates
                  </T>
                </Link>
                .
              </p>
            </div>
          )}

          {/* Subject only applies without a template; a selected template
              owns its own subject (same convention as the Approval step). */}
          {!emailTemplateId && (
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <Label
                  htmlFor="notifyEmailSubject"
                  className="text-sm font-medium"
                >
                  <T context="notify step config field">Email subject</T>
                </Label>
                <p className="text-[11px] text-muted-foreground">
                  <T context="optional field hint">Optional</T>
                </p>
              </div>
              <Input
                id="notifyEmailSubject"
                value={emailSubject}
                onChange={(e) => onChange({ emailSubject: e.target.value })}
                placeholder={
                  emailSubjectHint ??
                  gt('Defaults to the title', {
                    context: 'notify email subject placeholder',
                  })
                }
              />
            </div>
          )}
        </div>
      )}
    </>
  );
}
